import React, { useState, useEffect } from "react";
import "./FeedStyle.css";
import { observer } from "mobx-react";
import { Formik } from "formik";
import * as yup from "yup";
import { Navigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRss, faGlobe, faTag, faPlus, faExternalLinkAlt, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFeedsStore } from "../../feedstore";

import ReactGA from 'react-ga4';

const schema = yup.object({
  name: yup.string().required("Feed name is required"),
  url: yup
    .string()
    .required("URL is required")
    .matches(
      /(https?:\/\/)?([\w-])+\.{1}([a-zA-Z]{2,63})([/\w-]*)*\/?\??([^#\n\r]*)?#?([^\n\r]*)/,
      "Invalid URL"
    ),
});

function HomePage() {
  const feedsStore = useFeedsStore();
  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname + window.location.search });
  }, []);

  const [initialized, setInitialized] = useState(false);
  const [redirectToFeed, setRedirectToFeed] = useState(false);

  const handleSubmit = async (values, { resetForm }) => {
    const isValid = await schema.validate(values);
    if (!isValid) {
      return;
    }
    feedsStore.feeds.push(values);
    feedsStore.setFeeds([...feedsStore.feeds]);
    localStorage.setItem("feeds", JSON.stringify(feedsStore.feeds));
    if (resetForm) {
      resetForm();
    }
  };

  const setSelectedFeed = url => {
    feedsStore.setSelectedFeed(url);
    setRedirectToFeed(true);
  };

  const deleteFeed = index => {
    feedsStore.feeds.splice(index, 1);
    feedsStore.setFeeds([...feedsStore.feeds]);
    localStorage.setItem("feeds", JSON.stringify(feedsStore.feeds));
  };

  useEffect(() => {
    if (!initialized) {
      let rssFeeds = [];
      try {
        rssFeeds = JSON.parse(localStorage.getItem("feeds"));
        if (Array.isArray(rssFeeds)) {
          feedsStore.setFeeds(rssFeeds);
        }
      } catch (ex) {}
      setInitialized(true);
    }
  }, [initialized, feedsStore]);

  if (redirectToFeed) {
    return (
      <Navigate to={`/feed/feedpage?url=${encodeURIComponent(feedsStore.feed)}`} />
    );
  }

  return (
    <div className="home-page bg-light py-5 min-vh-100 mt-5">
      <div className="container feeds-container">
        <div className="row justify-content-center">
          <div className="col-12">
            <div className="card shadow-sm border-0 rounded-3 mb-4">
              <div className="card-header bg-white border-bottom-0 pt-4 pb-2 text-center">
                <div className="text-warning mb-2 display-4">
                  <FontAwesomeIcon icon={faRss} />
                </div>
                <h3 className="fw-light my-2">RSS Feeds</h3>
                <p className="text-muted small mb-0">Stay updated with Personalized Cloud Dashboard (<abbr title="Personalized Cloud Dashboard">PCD</abbr>) health & news feeds!</p>
              </div>

              <div className="card-body px-4 pb-4">
                <Formik 
                  initialValues={{ name: "", url: "" }}
                  validationSchema={schema} 
                  onSubmit={handleSubmit}
                >
                  {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    values,
                    touched,
                    errors,
                  }) => (
                    <form noValidate onSubmit={handleSubmit}>
                      <div className="row g-3 mb-4">
                        <div className="col-md-6">
                          <label htmlFor="feed-name" className="form-label small text-muted">Feed Name</label>
                          <div className="input-group input-group-alternative">
                            <span className="input-group-text"><FontAwesomeIcon icon={faTag} className="text-muted" /></span>
                            <input
                              className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                              placeholder="e.g. Health & Fitness News"
                              type="text"
                              id="feed-name"
                              name="name"
                              value={values.name || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                            />
                          </div>
                          {touched.name && errors.name && (
                            <div className="text-danger small mt-1">{errors.name}</div>
                          )}
                        </div>

                        <div className="col-md-6">
                          <label htmlFor="feed-url" className="form-label small text-muted">Feed URL</label>
                          <div className="input-group input-group-alternative">
                            <span className="input-group-text"><FontAwesomeIcon icon={faGlobe} className="text-muted" /></span>
                            <input
                              className={`form-control ${touched.url && errors.url ? 'is-invalid' : ''}`}
                              placeholder="https://example.com/rss.xml"
                              type="text"
                              id="feed-url"
                              name="url"
                              value={values.url || ""}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              required
                            />
                          </div>
                          {touched.url && errors.url && (
                            <div className="text-danger small mt-1">{errors.url}</div>
                          )}
                        </div>
                      </div>

                      <div className="text-center">
                        <button type="submit" id="add-feed-btn" className="btn btn-warning shadow-sm py-2 px-5 fw-bold text-uppercase">
                          <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Feed
                        </button>
                      </div>
                    </form>
                  )}
                </Formik>
              </div>
            </div>

            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="fw-light text-muted mb-0">Active Feeds ({feedsStore.feeds.length})</h5>
              </div>
              {feedsStore.feeds.length === 0 ? (
                <div className="card shadow-sm border-0 rounded-3 text-center py-5 px-3 bg-white">
                  <div className="text-warning mb-2 display-6">
                    <FontAwesomeIcon icon={faRss} className="opacity-50" />
                  </div>
                  <p className="text-muted mb-0">No feeds configured yet. Add a feed URL above to get started.</p>
                </div>
              ) : (
                feedsStore.feeds.map((f, i) => (
                  <div className="card shadow-sm border-0 rounded-3 mb-3 bg-white" key={i}>
                    <div className="card-body p-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                      <div className="flex-grow-1 me-md-3">
                        <div className="d-flex align-items-center gap-2 mb-1">
                          <h5 className="card-title fw-bold text-dark mb-0">{f.name}</h5>
                          <span className="badge bg-warning text-dark">RSS</span>
                        </div>
                        <p className="text-muted small text-break mb-0">{f.url}</p>
                      </div>
                      <div className="d-flex gap-2 flex-shrink-0">
                        <button
                          type="button"
                          className="btn btn-warning btn-sm shadow-sm fw-bold px-3 py-2"
                          onClick={() => setSelectedFeed(f.url)}
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" /> Open
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm shadow-sm px-3 py-2"
                          onClick={() => deleteFeed(i)}
                        >
                          <FontAwesomeIcon icon={faTrashAlt} className="me-1" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default observer(HomePage);